import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import { WidgetViewState } from '@/components/widget/WidgetOpeningManager/types';
import useChannels from '@/hooks/useChannels';
import { useScrollbar } from '@/hooks/useScrollbar';
import { useWidgetUIStore } from '@/hooks/useWidgetUIStore';
import { ChannelOptions } from '@/types/widgetConfig';
import QRCodeDisplay from './QRCodeDisplay';
import type { ChannelDetailProps } from './types';

/**
 * ChannelDetail component displays QR code and information for a specific messaging channel
 * Redesigned to match Figma design with simplified layout and styling
 *
 * Features:
 * - Clean header with channel name and back button
 * - Simplified QR code display without gradient effects
 * - Dynamic title and description based on channel type
 * - "Powered by SleekFlow" footer
 */
const GeneralChannelDetail: React.FC<ChannelDetailProps> = ({
  onMinimize,
  onContinue,
  onBack,
  className = '',
}) => {
  const { t } = useTranslation();
  const scrollbar = useScrollbar();
  const { currentChannelDetailType, actions } = useWidgetUIStore();

  const { channelUrl, copywriting, qrCodeImage } = useChannels({
    channelType: currentChannelDetailType as keyof ChannelOptions,
  });

  // Handle back navigation
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      actions.setCurrentView(WidgetViewState.LANDING_PAGE);
      actions.setChannelDetailType(null);
    }
  };

  // Handle continue action
  const handleContinue = () => {
    if (channelUrl) {
      // Open channel URL in new window/tab
      window.open(channelUrl, '_blank', 'noopener,noreferrer');
    }
    onContinue?.();
  };

  return (
    <div
      className={classNames(
        'sf:h-full sf:flex sf:flex-col sf:background-gradient-diagonal sf:overflow-y-auto',
        'sf:sm:rounded-2xl',
        className,
      )}
      style={scrollbar.scrollbarStyle}
      onMouseEnter={scrollbar.onMouseEnter}
      onMouseLeave={scrollbar.onMouseLeave}
    >
      {/* Header - Simplified design matching Figma */}
      <Header
        onBack={handleBack}
        title={copywriting.title}
        onMinimize={onMinimize}
      />

      {/* Main Content - Centered Layout */}
      <div className="sf:flex-1 sf:flex sf:flex-col sf:items-center sf:justify-center sf:p-6 sf:text-center">
        {/* Title */}
        <h1 className="sf:text-2xl sf:font-bold sf:text-gray-900 sf:mb-2 sf:max-w-sm sf:leading-tight">
          {copywriting.title}
        </h1>

        {/* Description */}
        <p className="sf:text-gray-600 sf:mb-3 sf:max-w-sm sf:leading-relaxed">
          {copywriting.description}
        </p>

        {/* QR Code - Simplified without gradient effects */}
        <div className="sf:mb-8">
          <QRCodeDisplay
            imageUrl={qrCodeImage}
            value={channelUrl}
            className="sf:rounded-lg sf:border sf:border-gray-200"
            errorMessage={t(
              'channelDetail.common.qrCodeError',
              'Unable to generate QR code',
            )}
          />
        </div>

        {channelUrl && (
          <>
            <div className="sf:w-full sf:flex sf:items-center sf:justify-center sf:gap-2 sf:mb-4">
              <div className="sf:w-full sf:h-0.5 sf:bg-gray-30"></div>
              <p className="sf:text-gray-500 sf:text-sm">
                {t('channelDetail.common.or', 'or')}
              </p>
              <div className="sf:w-full sf:h-0.5 sf:bg-gray-30"></div>
            </div>

            <div className="sf:w-full sf:flex sf:items-center sf:justify-center sf:gap-2 sf:mb-4">
              {copywriting.continueButtonDescription}
            </div>
            <button
              onClick={handleContinue}
              className="sf:border-widget-button-color sf:border-2 sf:text-widget-button-color sf:py-3 sf:px-6 sf:rounded-full sf:font-semibold sf:transition-all sf:duration-200 sf:transform sf:hover:scale-105 sf:active:scale-95 sf:focus:outline-none sf:focus:ring-4 sf:focus:ring-blue-300 sf:focus:ring-opacity-50"
              type="button"
            >
              {copywriting.continueButtonText}
            </button>
          </>
        )}
      </div>

      {/* Footer - "Powered by SleekFlow" */}
      <Footer />
    </div>
  );
};

export default GeneralChannelDetail;
